import { Fragment } from 'react';
import { Check, X } from 'lucide-react';
import { formatDate, formatNumber, type Checklist } from '@ste/shared';
import { SignatureImage } from '../../components/SignaturePad.tsx';
import { Card } from '../../components/ui.tsx';
import { t, type MessageKey } from '../../i18n/index.ts';
import logo from '../../theme/assets/logo.svg?raw';

const KINDS: Record<string, MessageKey> = {
  grid: 'protocol.kind.grid',
  pv: 'protocol.kind.pv',
  battery: 'protocol.kind.battery',
  charge: 'protocol.kind.charge',
  aux: 'protocol.kind.aux',
  ext: 'protocol.kind.ext',
  charger: 'protocol.kind.charger',
  vehicle: 'protocol.kind.vehicle',
};

const dateTime = (iso: string) =>
  new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });

/** signed commissioning protocol, read-only; same layout on screen, for customers and in print */
export function ProtocolDocument({ checklist }: { checklist: Checklist }) {
  const { snapshot, signatures } = checklist;
  return (
    <div className="space-y-4">
      <div className="hidden print:block">
        <div style={{ width: 180 }} dangerouslySetInnerHTML={{ __html: logo }} />
      </div>
      <Card title={t('protocol.title')}>
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
          <dt className="text-muted">{t('protocol.site')}</dt>
          <dd>{snapshot?.siteTitle ?? '–'}</dd>
          <dt className="text-muted">{t('checklist.customer')}</dt>
          <dd>{checklist.customer ?? '–'}</dd>
          <dt className="text-muted">{t('checklist.installer')}</dt>
          <dd>{checklist.installer ?? '–'}</dd>
          {checklist.completedAt && (
            <>
              <dt className="text-muted">{t('protocol.completedAt')}</dt>
              <dd>{dateTime(checklist.completedAt)}</dd>
            </>
          )}
          {snapshot?.evccVersion && (
            <>
              <dt className="text-muted">{t('protocol.software')}</dt>
              <dd>evcc {snapshot.evccVersion}</dd>
            </>
          )}
        </dl>
      </Card>

      {snapshot?.profile && <ProfileCard snapshot={snapshot} />}

      {snapshot && snapshot.devices.length > 0 && (
        <Card title={t('protocol.devices')}>
          <ul className="grid gap-1 text-sm sm:grid-cols-2">
            {snapshot.devices.map((d) => (
              <li key={`${d.kind}-${d.name}`}>
                <span className="text-muted">{KINDS[d.kind] ? t(KINDS[d.kind]!) : d.kind}:</span> {d.name}
              </li>
            ))}
          </ul>
          {snapshot.measurements && (
            <p className="mt-3 text-sm text-muted">
              {t('protocol.measurements')}: {snapshot.measurements}
            </p>
          )}
        </Card>
      )}

      <Card title={t('protocol.checks')}>
        <ul className="divide-y divide-border">
          {checklist.items.map((item) => (
            <li key={item.id} className="flex gap-3 py-2 text-sm">
              {item.done ? (
                <Check
                  size={18}
                  className="mt-0.5 shrink-0 text-accent-text"
                  aria-label={t('protocol.done')}
                />
              ) : (
                <X size={18} className="mt-0.5 shrink-0 text-warm-text" aria-label={t('protocol.notDone')} />
              )}
              <div>
                <div>{item.label}</div>
                {item.note && <div className="text-muted">{item.note}</div>}
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 print:grid-cols-2">
        {(['installer', 'customer'] as const).map((role) => {
          const signature = signatures?.[role];
          return (
            <Card
              key={role}
              title={t(role === 'installer' ? 'protocol.signInstaller' : 'protocol.signCustomer')}
            >
              {signature ? (
                <>
                  <div className="rounded-lg bg-white">
                    <SignatureImage
                      path={signature.path}
                      label={`${t('protocol.signature')} ${signature.name}`}
                    />
                  </div>
                  <p className="mt-2 border-t border-border pt-2 text-sm">
                    {signature.name} · {dateTime(signature.signedAt)}
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted">{t('protocol.notSigned')}</p>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/** master data, house connection and §14a at handover (ADR-0017) */
function ProfileCard({ snapshot }: { snapshot: NonNullable<Checklist['snapshot']> }) {
  const p = snapshot.profile!;
  const address = [p.street, [p.zip, p.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const rows: [MessageKey, string | undefined][] = [
    ['profile.customerName', p.customerName],
    ['profile.street', address],
    ['profile.gridOperator', p.gridOperator],
    ['profile.meterNumber', p.meterNumber],
    ['profile.commissionedAt', p.commissionedAt ? formatDate(new Date(p.commissionedAt)) : undefined],
    ['profile.installerName', p.installerName],
    [
      'protocol.houseConnection',
      snapshot.houseConnectionA !== undefined ? `${formatNumber(snapshot.houseConnectionA)} A` : undefined,
    ],
    ['protocol.control14a', snapshot.control14a],
    ['house.module', p.module14a ? t(`house.module.${p.module14a}`) : undefined],
  ];
  return (
    <Card title={t('protocol.profile')}>
      <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
        {rows
          .filter(([, value]) => value)
          .map(([label, value]) => (
            <Fragment key={label}>
              <dt className="text-muted">{t(label)}</dt>
              <dd>{value}</dd>
            </Fragment>
          ))}
      </dl>
    </Card>
  );
}
